import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { useReferral } from "@/context/ReferralContext";
import { track } from "@/lib/analytics";
import { ArrowRight, CheckCircle2, Building2 } from "lucide-react";

export default function ResellerLanding() {
  const { slug } = useParams();
  const { attach } = useReferral();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/public/reseller-landing/${slug}`)
      .then((r) => {
        setData(r.data);
        track("referral_landing_view", { landing_slug: slug, owner_type: "reseller" });
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto py-24 px-6 text-center">
          <h1 className="text-3xl font-bold text-[#0B0B0B]">Reseller no encontrado</h1>
          <Link to="/" className="mt-6 inline-block text-[#F26A21] hover:underline">Volver al inicio</Link>
        </div>
      </PublicLayout>
    );
  }
  if (!data) {
    return <PublicLayout><div className="py-24 text-center text-gray-500">Cargando…</div></PublicLayout>;
  }

  const goToCatalog = async () => {
    if (data.primary_code) await attach(data.primary_code);
    window.location.href = "/catalogo";
  };

  return (
    <PublicLayout>
      <section className="bg-[#0B0B0B] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F26A21]/15 border border-[#F26A21]/40">
            <Building2 className="w-3.5 h-3.5 text-[#F26A21]" aria-hidden="true" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#F26A21]">
              Aliado comercial
            </span>
          </div>
          <h1 className="mt-6 text-5xl font-extrabold tracking-tight font-display">
            {data.company_name}
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-gray-300 leading-relaxed">
            {data.contact_name} y su equipo distribuyen los 193 programas de Debt Capital College
            con soporte comercial y educativo local. Todos los programas mantienen la calidad
            académica DCC + IADA · Anáhuac Business School.
          </p>
          {data.primary_code && (
            <div className="mt-6 inline-flex items-center gap-3 px-4 py-2 rounded-md bg-[#F26A21]/15 border border-[#F26A21]/50">
              <CheckCircle2 className="w-4 h-4 text-[#F26A21]" aria-hidden="true" />
              <span className="text-sm">
                Código activo:
                <span className="ml-2 font-mono font-bold text-[#F26A21]" data-testid="reseller-code">
                  {data.primary_code}
                </span>
              </span>
            </div>
          )}
          <div className="mt-8">
            <button
              onClick={goToCatalog}
              data-testid="reseller-cta-catalog"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm transition-all hover:scale-[1.02]"
            >
              Explora el catálogo <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl font-bold text-[#0B0B0B]">¿Cómo funciona la relación con {data.company_name}?</h2>
        <div className="mt-6 grid md:grid-cols-3 gap-4">
          {[
            ["1", "Cotización local", "Recibe atención comercial en tu región. Todos los precios están alineados al catálogo DCC."],
            ["2", "Programas 100% DCC", "El contenido, docentes y credenciales son los mismos que DCC directo. Sin diluciones de calidad."],
            ["3", "Facturación transparente", "Puedes optar por facturar con DCC o con el aliado, según tu política interna."],
          ].map(([n, t, d]) => (
            <div key={n} className="p-5 rounded-2xl border border-gray-200 bg-[#F9FAFB]">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#F26A21]">Paso {n}</p>
              <h3 className="mt-2 text-lg font-bold text-[#0B0B0B]">{t}</h3>
              <p className="mt-2 text-sm text-gray-600">{d}</p>
            </div>
          ))}
        </div>
      </section>
    </PublicLayout>
  );
}
